import { useCallback, useEffect, useState } from 'react';
import { useAdminApi, ApiError } from '../../shared/adminApi';

/**
 * Every metric arrives twice: once counting notifications and once counting
 * distinct users (`*_users`). The page's Total / Distinct users toggle picks a
 * family — it never refetches, because both are in this one response.
 *
 * Distinct counts are computed per grain by the database. Do NOT sum them
 * across rows: a user who received both a deal and an announcement is one user
 * in `totals` and two across `by_type`.
 */
export interface OpenStatsRow {
  notification_type: string;
  /** 'business' | 'admin_broadcast' | 'app_push' */
  bucket?: string;
  delivered: number;
  /** Delivered rows inside the instrumented era — the denominator for open_rate. */
  delivered_tracked: number;
  opened: number;
  fulfilled: number;
  engaged: number;
  failed: number;
  throttled: number;
  attempted: number;
  /** null when nothing was delivered — an UNKNOWN rate, not 0%. */
  open_rate: number | null;
  engaged_rate: number | null;
  delivery_rate: number | null;

  // Distinct-user family.
  delivered_users: number;
  delivered_tracked_users: number;
  opened_users: number;
  fulfilled_users: number;
  /** NOT opened_users + fulfilled_users — one person can do both. */
  engaged_users: number;
  failed_users: number;
  throttled_users: number;
  attempted_users: number;
  open_rate_users: number | null;
  engaged_rate_users: number | null;
  delivery_rate_users: number | null;
}

export interface FailureReason {
  reason: string;
  status: 'failed' | 'throttled' | string;
  count: number;
  /** Distinct users who hit this reason. */
  users: number;
  label: string;
  detail: string;
  /** 'gap' = something is broken; 'intended' = we chose not to send; 'unknown' = new reason. */
  fault: 'gap' | 'intended' | 'unknown' | string;
}

/**
 * How much of the user base we can reach at all — the question the rates cannot
 * answer, because a rate is only ever measured over people we already reached.
 *
 * Counted over live accounts only (not soft-deleted). Every `*_users` field
 * below that depends on the device table is null — never 0 — when that table
 * could not be read: "we don't know" must not render as "nobody".
 */
export interface Audience {
  total_users: number;
  active_users: number;
  dormant_users: number;
  never_seen_users: number;
  dormant_days: number;
  dormant_pct: number | null;
  /** Users with >=1 registered device. The best proxy we have for "push is on". */
  reachable_users: number | null;
  reachable_pct: number | null;
  unreachable_users: number | null;
  /** Seen recently AND reachable — the genuinely addressable audience. */
  active_reachable_users: number | null;
  device_rows_users: number | null;
  /** Device rows whose user no longer exists as a live account. */
  orphan_device_users: number | null;
}

/**
 * Which recipients the delivery numbers were measured over.
 *
 * A narrowed scope answers "of the people we can actually reach, how are we
 * doing" — but it also removes most `no_devices` failures, which are the
 * clearest evidence that the reachable base is small. `hidden_rows` and
 * `hidden_users` are what the view left out, and the page states them so a
 * filtered screenshot can't be read as the whole picture.
 */
export interface ScopeInfo {
  requested: RecipientScope;
  applied: RecipientScope;
  /** Users in scope; null when no narrowing applied. */
  users: number | null;
  hidden_rows: number;
  hidden_users: number;
  /** True when a scope was asked for but could not be resolved. */
  downgraded: boolean;
}

export interface OpenStats {
  window: string;
  scope: ScopeInfo;
  from: string;
  to: string;
  tracking_since: string;
  audience: Audience;
  /** null when no deal notifications were sent in the window at all. */
  deals: OpenStatsRow | null;
  by_type: OpenStatsRow[];
  totals: OpenStatsRow;
  buckets: StatsBucket[];
  failure_reasons: FailureReason[];
  deal_failure_reasons: FailureReason[];
}

export interface StatsBucket {
  bucket: string;
  label: string;
  rows: OpenStatsRow[];
  totals: OpenStatsRow;
}

/**
 * 'all' counts every send attempted. 'reachable' keeps only users who hold a
 * registered device; 'active' further requires they opened the app in the last
 * 30 days. Anything unrecognised widens back to 'all' server-side — a typo must
 * never quietly hide failures.
 */
export type RecipientScope = 'all' | 'reachable' | 'active';

/** Either a trailing window (`days`, or 'all') or an explicit inclusive range. */
export type StatsWindow = { days: number | 'all' } | { from: string; to: string };

/**
 * Notification delivery + open rates. Prod only — these lambdas follow the
 * Socials/attribution precedent and have no `-dev` variant, and prod is the
 * only place the numbers mean anything.
 */
export function useNotificationOpenStats(window: StatsWindow, scope: RecipientScope = 'all') {
  const { request } = useAdminApi();
  const [data, setData] = useState<OpenStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(
        await request<OpenStats>({
          env: 'prod',
          path: '/admin/notifications/open-stats',
          query: {
            ...('days' in window
              ? { days: String(window.days) }
              : { from: window.from, to: window.to }),
            scope,
          },
        }),
      );
    } catch (e) {
      setData(null);
      setError(e instanceof ApiError ? e.message : 'Could not load open stats');
    } finally {
      setLoading(false);
    }
    // Serialised so a fresh object literal with the same window doesn't refetch
    // on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request, JSON.stringify(window), scope]);

  useEffect(() => {
    void load();
  }, [load]);

  return { data, loading, error, reload: load };
}
